import { useCallback, useState } from 'react';
import {
  bontotalen,
  evenementdag,
  maakDemodataset,
  maakLegeBon,
  rondBonAf,
  voegProductToe,
  zetIngeleverdeBekers,
  type BtwTarief,
} from '@qc/core';
import { supabase } from '../supabase.js';

export interface Demoresultaat {
  evenementId: string;
  aantalKramen: number;
  aantalBonnen: number;
  inlogcodes: { kraam: string; code: string }[];
}

export interface Demobeheer {
  bezig: boolean;
  stap: string | null;
  fout: string | null;
  gevuld: Demoresultaat | null;
  gewist: { evenementen: number; bonnen: number } | null;
  vulDemodata: () => Promise<void>;
  wisDemodata: () => Promise<void>;
}

/**
 * De codes waarvoor in de seed al een kassa-inlog bestaat. De eerste twee
 * demokramen krijgen die codes, zodat je meteen op een toestel kunt inloggen
 * zonder eerst accounts aan te maken. Accounts uitgeven hoort bij de
 * beheerkant en staat nog open.
 */
const BESTAANDE_INLOGS = ['DEMO-BROODJE', 'DEMO-BAR'];

export function useDemodata(onKlaar: () => Promise<void>): Demobeheer {
  const [bezig, setBezig] = useState(false);
  const [stap, setStap] = useState<string | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [gevuld, setGevuld] = useState<Demoresultaat | null>(null);
  const [gewist, setGewist] = useState<{ evenementen: number; bonnen: number } | null>(null);

  const vulDemodata = useCallback(async () => {
    setBezig(true);
    setFout(null);
    setGewist(null);

    try {
      // Regel 5: de demodag is de Amsterdamse datum, niet de UTC-datum. Anders
      // krijgt een vulling na middernacht (vóór 01:00 of 02:00) de dag ervoor.
      const vandaag = evenementdag(new Date());
      const demo = maakDemodataset({ dag: vandaag });

      setStap('Organisator en evenement aanmaken…');
      const organisatorId = crypto.randomUUID();
      const evenementId = crypto.randomUUID();

      const { error: organisatorFout } = await supabase.from('organisatoren').insert({
        id: organisatorId,
        bedrijfsnaam: 'Demo Evenementen BV',
        email: 'demo@example.com',
        iban: 'NL91ABNA0417164300',
        kvk_nummer: '12345678',
        btw_nummer: 'NL123456789B01',
        is_demo: true,
      });
      if (organisatorFout) throw new Error(organisatorFout.message);

      const { error: evenementFout } = await supabase.from('evenementen').insert({
        id: evenementId,
        organisator_id: organisatorId,
        naam: demo.evenement.naam,
        locatie: demo.evenement.locatie,
        start_op: demo.evenement.startOp,
        eind_op: demo.evenement.eindOp,
        status: 'actief',
        bekerborg_cent: demo.evenement.bekerborgCent,
        dagtarief_kraamset_cent: demo.evenement.dagtariefKraamsetCent,
        transactiekosten_per_bon_tiende_cent: demo.evenement.doorbelastPerBonTiendeCent,
        transactiekosten_over_omzet_bp: demo.evenement.doorbelastOverOmzetBasispunten,
        inkoop_over_omzet_bp: demo.evenement.inkoopOverOmzetBasispunten,
        is_demo: true,
      });
      if (evenementFout) throw new Error(evenementFout.message);

      setStap('Kramen en menu’s klaarzetten…');
      const kraamIds = demo.kramen.map(() => crypto.randomUUID());
      const deelnameIds = demo.kramen.map(() => crypto.randomUUID());
      const menuIds = demo.kramen.map(() => crypto.randomUUID());
      const productIds = demo.kramen.map((kraam) => kraam.producten.map(() => crypto.randomUUID()));

      // KvK en BTW zijn nep maar van de goede vorm (BTW doorstaat de elfproef):
      // zonder die twee weigert de afrekening terecht een factuur op naam van
      // de kraam te maken, en dan toont de Afrekening-tab in de demo een fout.
      const DEMO_KVK = ['80000001', '80000003', '80000004', '80000005'];
      const DEMO_BTW = ['NL800000018B01', 'NL800000031B01', 'NL800000043B01', 'NL800000055B01'];
      const { error: kraamFout } = await supabase.from('kramen').insert(
        demo.kramen.map((kraam, index) => ({
          id: kraamIds[index] ?? '',
          naam: kraam.naam,
          iban: 'NL02ABNA0123456789',
          kvk_nummer: DEMO_KVK[index] ?? '80000001',
          btw_nummer: DEMO_BTW[index] ?? 'NL800000018B01',
          contactpersoon: `Demo ${kraam.naam}`,
          email: 'info@qualitycashless.com',
          selfbilling_akkoord: true,
          is_demo: true,
        })),
      );
      if (kraamFout) throw new Error(kraamFout.message);

      const inlogcodes = demo.kramen.map(
        (kraam, index) => BESTAANDE_INLOGS[index] ?? kraam.inlogcode,
      );

      const { error: deelnameFout } = await supabase.from('kraamdeelnames').insert(
        demo.kramen.map((kraam, index) => ({
          id: deelnameIds[index] ?? '',
          evenement_id: evenementId,
          kraam_id: kraamIds[index] ?? '',
          commissie_bp: kraam.commissieBasispunten,
          status: 'actief' as const,
          inlogcode: inlogcodes[index] ?? kraam.inlogcode,
        })),
      );
      if (deelnameFout) throw new Error(deelnameFout.message);

      const { error: menuFout } = await supabase.from('menus').insert(
        demo.kramen.map((_, index) => ({
          id: menuIds[index] ?? '',
          kraamdeelname_id: deelnameIds[index] ?? '',
          versie: 1,
          status: 'goedgekeurd' as const,
          goedgekeurd_op: new Date().toISOString(),
        })),
      );
      if (menuFout) throw new Error(menuFout.message);

      const { error: itemFout } = await supabase.from('menu_items').insert(
        demo.kramen.flatMap((kraam, kraamIndex) =>
          kraam.producten.map((product, productIndex) => ({
            id: productIds[kraamIndex]?.[productIndex] ?? '',
            menu_id: menuIds[kraamIndex] ?? '',
            naam: product.naam,
            prijs_cent: product.prijsCent,
            btw_tarief: product.btwTarief,
            categorie: product.categorie,
            volgorde: productIndex,
            bekers: product.bekers,
          })),
        ),
      );
      if (itemFout) throw new Error(itemFout.message);

      setStap('Omzet van de dag aanslaan…');
      const bonrijen = [];
      const regelrijen = [];
      const betalingrijen = [];

      for (const demobon of demo.bonnen) {
        const kraam = demo.kramen[demobon.kraamIndex];
        const deelnameId = deelnameIds[demobon.kraamIndex];
        if (kraam === undefined || deelnameId === undefined) continue;

        // Via dezelfde weg als de kassa, zodat de bedragen precies zo worden
        // berekend als in het echt.
        let bon = maakLegeBon({
          clientUuid: crypto.randomUUID(),
          kraamdeelnameId: deelnameId,
          evenementdag: demo.evenement.startOp,
          aangeslagenOp: demobon.aangeslagenOp,
          bekerborgCent: demo.evenement.bekerborgCent,
        });
        for (const regel of demobon.regels) {
          const product = kraam.producten[regel.productIndex];
          if (product === undefined) continue;
          bon = voegProductToe(
            bon,
            {
              menuItemId: productIds[demobon.kraamIndex]?.[regel.productIndex] ?? '',
              omschrijving: product.naam,
              stukprijsCent: product.prijsCent,
              btwTarief: product.btwTarief as BtwTarief,
              bekers: product.bekers,
            },
            crypto.randomUUID(),
            regel.aantal,
          );
        }
        if (demobon.ingeleverdeBekers > 0) {
          bon = zetIngeleverdeBekers(bon, demobon.ingeleverdeBekers);
        }
        bon = rondBonAf(bon, demobon.nummer, demobon.aangeslagenOp);

        const totalen = bontotalen(bon);
        bonrijen.push({
          id: bon.clientUuid,
          client_uuid: bon.clientUuid,
          kraamdeelname_id: deelnameId,
          nummer: bon.nummer ?? 0,
          evenementdag: bon.evenementdag,
          totaal_cent: totalen.totaalCent,
          statiegeld_cent: totalen.statiegeldSaldoCent,
          status: 'afgerond' as const,
          aangeslagen_op: bon.aangeslagenOp,
        });
        regelrijen.push(
          ...bon.regels.map((regel, volgorde) => ({
            id: regel.regelId,
            bon_id: bon.clientUuid,
            menu_item_id: regel.menuItemId,
            omschrijving: regel.omschrijving,
            aantal: regel.aantal,
            stukprijs_cent: regel.stukprijsCent,
            btw_tarief: regel.btwTarief,
            regeltotaal_cent: regel.stukprijsCent * regel.aantal,
            volgorde,
          })),
        );
        if (totalen.totaalCent > 0) {
          betalingrijen.push({
            id: crypto.randomUUID(),
            client_uuid: crypto.randomUUID(),
            bon_id: bon.clientUuid,
            bedrag_cent: totalen.totaalCent,
            status: 'geslaagd' as const,
            provider: 'nep',
            afgerond_op: bon.aangeslagenOp,
          });
        }
      }

      for (let i = 0; i < bonrijen.length; i += 200) {
        const { error } = await supabase.from('bonnen').insert(bonrijen.slice(i, i + 200));
        if (error) throw new Error(error.message);
      }
      for (let i = 0; i < regelrijen.length; i += 300) {
        const { error } = await supabase.from('bonregels').insert(regelrijen.slice(i, i + 300));
        if (error) throw new Error(error.message);
      }
      for (let i = 0; i < betalingrijen.length; i += 200) {
        const { error } = await supabase.from('betalingen').insert(betalingrijen.slice(i, i + 200));
        if (error) throw new Error(error.message);
      }

      setStap('Kassa-inlogs koppelen…');
      // De bestaande demo-inlogs wijzen naar de nieuwe kraamdeelnames, zodat je
      // meteen op een toestel kunt inloggen.
      for (const [index, naam] of demo.kramen.slice(0, BESTAANDE_INLOGS.length).entries()) {
        const { error } = await supabase
          .from('profielen')
          .update({ kraamdeelname_id: deelnameIds[index] ?? null })
          .eq('rol', 'vendor')
          .eq('naam', naam.naam);
        if (error) throw new Error(error.message);
      }

      setGevuld({
        evenementId,
        aantalKramen: demo.kramen.length,
        aantalBonnen: bonrijen.length,
        inlogcodes: demo.kramen.map((kraam, index) => ({
          kraam: kraam.naam,
          code: inlogcodes[index] ?? kraam.inlogcode,
        })),
      });
      setStap(null);
      await onKlaar();
    } catch (oorzaak) {
      setFout(oorzaak instanceof Error ? oorzaak.message : 'De demodata kon niet worden gevuld.');
      setStap(null);
    } finally {
      setBezig(false);
    }
  }, [onKlaar]);

  const wisDemodata = useCallback(async () => {
    setBezig(true);
    setFout(null);
    setGevuld(null);

    const { data, error } = await supabase.rpc('wis_demodata');
    setBezig(false);

    if (error) {
      setFout(error.message);
      return;
    }
    const uitslag = data as { evenementen: number; bonnen: number } | null;
    setGewist(uitslag ?? { evenementen: 0, bonnen: 0 });
    await onKlaar();
  }, [onKlaar]);

  return { bezig, stap, fout, gevuld, gewist, vulDemodata, wisDemodata };
}
